import * as React from 'react';

import { useTracking } from '@xing-com/crate-communication-tracking';
import type {
  NWTEvent,
  PageViewTrackingEvent,
} from '@xing-com/crate-communication-tracking';
import type {
  NWTExtendedEvent,
  NWTExperimentEvent,
} from '@xing-com/crate-communication-tracking/src/nw-tracking-types';

type TrackNetworkNWTProps =
  | NWTEvent
  | {
      event: string;
    };

type TrackNetworkNWT = (props: TrackNetworkNWTProps) => void;

type TrackNetworkNWTExtended = (props: Partial<NWTExtendedEvent>) => void;

type TrackNetworkNWTExperiment = (props: Partial<NWTExperimentEvent>) => void;

type TrackNetwork = (
  props: PageViewTrackingEvent & { PropContextDimension1?: string }
) => void;

type UseNetworkTracking = () => {
  trackNetworkNWT: TrackNetworkNWT;
  trackNetworkNWTExtended: TrackNetworkNWTExtended;
  trackNetworkNWTExperiment: TrackNetworkNWTExperiment;
  trackNetwork: TrackNetwork;
};

export const useNetworkTracking: UseNetworkTracking = () => {
  const { track } = useTracking();

  const trackNetworkNWT: TrackNetworkNWT = React.useCallback(
    (props) => {
      track({
        type: 'nwt',
        eventSchema: 'basic',
        sentBy: 'network',
        page: 'network/network',
        application: 'wbm',
        ...props,
      });
    },
    [track]
  );

  const trackNetworkNWTExtended: TrackNetworkNWTExtended = React.useCallback(
    (props) => {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      track({
        type: 'nwt',
        eventSchema: 'extended',
        sentBy: 'network',
        page: 'network/network',
        application: 'wbm',
        ...props,
      } as NWTExtendedEvent);
    },
    [track]
  );

  const trackNetworkNWTExperiment: TrackNetworkNWTExperiment =
    React.useCallback(
      (props) => {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        track({
          type: 'nwt',
          eventSchema: 'experiment',
          sentBy: 'network',
          event: 'experiment_fetched',
          page: 'network/network',
          application: 'wbm',
          ...props,
        } as NWTExperimentEvent);
      },
      [track]
    );

  const trackNetwork: TrackNetwork = React.useCallback(
    (props) => {
      track({
        type: 'pageview',
        channel: 'wbm/Network',
        ...props,
      });
    },
    [track]
  );

  return {
    trackNetwork,
    trackNetworkNWT,
    trackNetworkNWTExtended,
    trackNetworkNWTExperiment,
  };
};
